import { buildTheme }                                  from './theme';
import { createInitialState, applyMove }               from './game';
import { computeLayout, renderFrame, ANIM_TOTAL_MS }   from './renderer';
import { attachSwipeListener }                          from './input';
import { PALETTES, PALETTE_ORDER }                     from './palettes';
import { t }                                            from './i18n';
import { track }                                        from './analytics';
import { SCENES }                                       from './scenes';
import { sceneId, track as ctrack, canInstall, promptInstall } from './campaign';
import type { GameState, Theme }                       from './types';
import type { PaletteId }                              from './palettes';
import type { Direction }                              from './types';

// ── Config ────────────────────────────────────────────────────────────────────

const BEST_SCORE_KEY    = 'cld2048_best';
const PALETTE_KEY       = 'cld2048_palette';
const MAX_DPR           = 2;    // clamp DPR — halves pixels on 3x screens, no visible loss

const SHAKE_MS          = 380;
const SHAKE_AMPLITUDE   = 3;    // px — subtle, just enough to signal "no move"
const SHAKE_FREQ        = 3;    // oscillations over SHAKE_MS

// ── Canvas setup ──────────────────────────────────────────────────────────────

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx    = canvas.getContext('2d')!;

function dpr(): number {
  return Math.min(window.devicePixelRatio || 1, MAX_DPR);
}

function resize() {
  const d = dpr();
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width          = Math.round(w * d);
  canvas.height         = Math.round(h * d);
  canvas.style.width    = w + 'px';
  canvas.style.height   = h + 'px';
  ctx.setTransform(d, 0, 0, d, 0, 0);
}

new ResizeObserver(resize).observe(document.body);
window.visualViewport?.addEventListener('resize', resize);
resize();

// ── Palette & theme ───────────────────────────────────────────────────────────

const storedPalette = localStorage.getItem(PALETTE_KEY) as PaletteId | null;
let paletteId: PaletteId = PALETTE_ORDER.includes(storedPalette as PaletteId)
  ? (storedPalette as PaletteId)
  : 'classic';

let theme: Theme = buildTheme(PALETTES[paletteId]);

function switchPalette(id: PaletteId) {
  paletteId = id;
  theme = buildTheme(PALETTES[id]);
  localStorage.setItem(PALETTE_KEY, id);

  // Update active dot in the switcher
  document.querySelectorAll<HTMLElement>('.pal-dot').forEach(btn => {
    btn.classList.toggle('active', btn.id === `pd-${id}`);
  });
}

// Initialise switcher to stored palette
switchPalette(paletteId);

document.getElementById('pd-classic')?.addEventListener('click',  () => switchPalette('classic'));
document.getElementById('pd-lavender')?.addEventListener('click', () => switchPalette('lavender'));
document.getElementById('pd-icy')?.addEventListener('click',      () => switchPalette('icy'));

// ── Game state ────────────────────────────────────────────────────────────────

const storedBest = parseInt(localStorage.getItem(BEST_SCORE_KEY) ?? '0', 10);
const initialBest = isNaN(storedBest) ? 0 : storedBest;

// ── Campaign landing (?scene) ───────────────────────────────────────────────
// Засеянное состояние из рекламы. При активной сцене прогресс/рекорд не пишем
// (сессия эфемерна, как dev-хук). campaignFlow — пока идём по кампанийной воронке;
// «Ещё разок» сбрасывает его в обычную игру.
const scene = sceneId ? SCENES[sceneId] ?? null : null;
const campaign = !!scene;
let campaignFlow = campaign;
let firstMoveTracked = false;
let winTracked = false;
let ctaShown = false;

function makeSeeded(best: number): GameState {
  return {
    tiles: scene!.tiles.map((tl, i) => ({ id: 100000 + i, value: tl.value, row: tl.row, col: tl.col })),
    score: scene!.score,
    bestScore: best,
    status: 'playing',
    animations: null,
    animStartMs: performance.now(),
  };
}

let state: GameState = campaign
  ? makeSeeded(initialBest)
  : { ...createInitialState(initialBest), animStartMs: performance.now() };

// ── Analytics (события раунда) ────────────────────────────────────────────────
let overSent = false;       // game_over шлём один раз на раунд
let recordBeaten = false;   // new_best — один раз, при первом превышении рекорда
let recordBaseline = state.bestScore;
function reportStart() {
  overSent = false;
  recordBeaten = false;
  recordBaseline = state.bestScore;
  track('game_start');
}
reportStart();

// ── Move queue ────────────────────────────────────────────────────────────────
// Allows swiping faster than animations complete: the latest swipe during
// an animation is queued and applied the moment the animation finishes.

let pendingDir: Direction | null = null;

function applyDir(dir: Direction, now: number) {
  const next = applyMove(state, dir);
  if (next === state) {
    startShake(now);
    return;
  }
  state = { ...next, animStartMs: now };
  if (!campaign) localStorage.setItem(BEST_SCORE_KEY, String(state.bestScore));

  if (!campaign && !recordBeaten && state.bestScore > recordBaseline) {
    recordBeaten = true;
    track('new_best', { score: state.bestScore });
  }
  if (!overSent && state.status !== 'playing') {
    overSent = true;
    track('game_over', { result: state.status === 'won' ? 'win' : 'lose', score: state.score });
  }
  if (campaignFlow && !firstMoveTracked) {
    firstMoveTracked = true;
    ctrack('campaign_first_move');
  }
}

// ── Shake (visual-only, lives outside GameState) ──────────────────────────────

let shakeStartMs: number | null = null;

function startShake(now: number) {
  shakeStartMs = now;
}

function computeShakeX(now: number): number {
  if (shakeStartMs === null) return 0;
  const t = (now - shakeStartMs) / SHAKE_MS;
  if (t >= 1) { shakeStartMs = null; return 0; }
  return SHAKE_AMPLITUDE * Math.sin(2 * Math.PI * SHAKE_FREQ * t) * (1 - t);
}

// ── Input ─────────────────────────────────────────────────────────────────────

attachSwipeListener(canvas, (dir) => {
  if (menuOverlay.classList.contains('open')) return;
  if (state.status !== 'playing') return;

  if (state.animations !== null) {
    // Animating: queue the move (overwrite any earlier queued move)
    pendingDir = dir;
    return;
  }

  applyDir(dir, performance.now());
});

// Tap/click to restart after win or lose
function onRestartTap(e: TouchEvent | MouseEvent) {
  if (campaignFlow) return;  // кампанийную победу разруливает CTA-оверлей
  if (state.status === 'won' || state.status === 'lost') {
    e.preventDefault();
    pendingDir = null;
    state = {
      ...createInitialState(state.bestScore),
      animStartMs: performance.now(),
    };
    reportStart();
  }
}

canvas.addEventListener('touchstart', onRestartTap as (e: TouchEvent) => void, { passive: false });
canvas.addEventListener('click', onRestartTap as (e: MouseEvent) => void);

// ── In-game menu (☰ → Resume / Restart / Home) ────────────────────────────────

const menuToggle  = document.getElementById('menu-toggle')  as HTMLButtonElement;
const menuOverlay = document.getElementById('menu-overlay') as HTMLDivElement;
const menuResume  = document.getElementById('menu-resume')  as HTMLButtonElement;
const menuRestart = document.getElementById('menu-restart') as HTMLButtonElement;
const menuHub     = document.getElementById('menu-hub')     as HTMLButtonElement;

menuResume.textContent  = t('resume');
menuRestart.textContent = t('restart_game');
menuHub.textContent     = t('to_hub');

function openMenu() {
  menuOverlay.classList.add('open');
}

function closeMenu() {
  menuOverlay.classList.remove('open');
}

menuToggle.addEventListener('click', (e) => {
  e.stopPropagation();
  openMenu();
});

menuOverlay.addEventListener('click', (e) => {
  // click on backdrop (not a button) closes the menu
  if (e.target === menuOverlay) closeMenu();
});

menuResume.addEventListener('click', (e) => {
  e.stopPropagation();
  closeMenu();
});

menuRestart.addEventListener('click', (e) => {
  e.stopPropagation();
  closeMenu();
  pendingDir = null;
  state = {
    ...createInitialState(state.bestScore),
    animStartMs: performance.now(),
  };
  reportStart();
});

menuHub.addEventListener('click', (e) => {
  e.stopPropagation();
  location.href = document.body.dataset.hubUrl ?? '/';
});

// ── Campaign overlays (hook + install CTA) ─────────────────────────────────────

const hookOverlay = document.getElementById('hook-overlay') as HTMLDivElement;
const hookTitle   = document.getElementById('hook-title')   as HTMLDivElement;
const hookPlay    = document.getElementById('hook-play')    as HTMLButtonElement;
const ctaOverlay  = document.getElementById('cta-overlay')  as HTMLDivElement;
const ctaTitle    = document.getElementById('cta-title')    as HTMLDivElement;
const ctaInstall  = document.getElementById('cta-install')  as HTMLButtonElement;
const ctaAgain    = document.getElementById('cta-again')    as HTMLButtonElement;

if (campaign && scene) {
  hookTitle.textContent  = t(scene.headlineKey);
  hookPlay.textContent   = t('hook_play');
  ctaTitle.textContent   = t('cta_win_title');
  ctaInstall.textContent = t('cta_install');
  ctaAgain.textContent   = t('cta_again');
  hookOverlay.classList.add('open');
  ctrack('campaign_land');
}

hookPlay.addEventListener('click', (e) => {
  e.stopPropagation();
  hookOverlay.classList.remove('open');
});

ctaInstall.addEventListener('click', (e) => {
  e.stopPropagation();
  promptInstall();
});

ctaAgain.addEventListener('click', (e) => {
  e.stopPropagation();
  ctrack('campaign_continue');
  campaignFlow = false;
  ctaOverlay.classList.remove('open');
  pendingDir = null;
  state = { ...createInitialState(state.bestScore), animStartMs: performance.now() };
});

// ── Render loop ───────────────────────────────────────────────────────────────

function loop(now: number) {
  // When an animation finishes, check if a move was queued
  if (state.animations !== null && now - state.animStartMs >= ANIM_TOTAL_MS) {
    state = { ...state, animations: null };

    if (pendingDir !== null && state.status === 'playing') {
      const dir  = pendingDir;
      pendingDir = null;
      applyDir(dir, now);
    }
  }

  menuToggle.hidden = state.status !== 'playing';

  // Кампанийная воронка: победа на засеянной доске → показать install-CTA.
  if (campaignFlow && state.status === 'won') {
    if (!winTracked) {
      winTracked = true;
      ctrack('campaign_seeded_win', { score: state.score });
    }
    if (!ctaOverlay.classList.contains('open')) {
      ctaInstall.style.display = canInstall() ? '' : 'none';
      ctaOverlay.classList.add('open');
      if (!ctaShown) {
        ctaShown = true;
        ctrack('campaign_install_prompt', { install_available: canInstall() });
      }
    }
  }

  const d      = dpr();
  const viewW  = canvas.width  / d;
  const viewH  = canvas.height / d;
  const layout = computeLayout(viewW, viewH);
  const shakeX = computeShakeX(now);

  renderFrame(ctx, viewW, viewH, layout, state, theme, now, shakeX);

  requestAnimationFrame(loop);
}

requestAnimationFrame(loop);
